import { useEffect, useState } from "react";
import useOnboardReqWithBearerToken from "lib/hooks/register/useOnBoardReqWithBearerToken";
import { getAllBoardList } from "lib/common/endpoints/public";
import useSWR from "swr";
import { useAppDispatch } from "redux/hooks";
import { hideAuthDrawer } from "redux/reducers/drawerSlice";
import Router from "next/router";
import { createClassroomSlug } from "utils/slugify";
import Button from "../atomic/Button";
import { ButtonSpinner } from "../atomic/Spinner/Spinner";
import { CheckBoxGroup, CheckBoxItem } from "../atomic/CheckBox/checkBox";
import Select from "../atomic/Select/Select";
import { Form } from "../Form";
import FormControl from "../Form/FormControl";
import Spacer from "../Form/Spacer";

interface PreferencesProps {
  nextTab?: () => void;
}

function Preferences({ nextTab }: PreferencesProps) {
  const { data: boards } = useSWR(getAllBoardList);
  const { reqWithBearerToken, success, loading } =
    useOnboardReqWithBearerToken();
  const dispatch = useAppDispatch();
  const [filledValue, setFilledValue] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState("");

  useEffect(() => {
    if (success) {
      dispatch(hideAuthDrawer());
      const selectedBoard = boards?.data?.find(
        (b: { _id: { $oid: string } }) => b._id.$oid === selectedBoardId,
      );
      if (selectedBoard) {
        Router.push(
          `/goal/${createClassroomSlug(selectedBoard.name, selectedBoard._id.$oid)}`,
        );
      } else if (selectedBoardId) {
        Router.push(`/goal/${selectedBoardId}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <div className="py-8 max-w-screen-sm">
      <Form
        onSubmit={(value) => {
          setSelectedBoardId(String(value.boardId ?? ""));
          reqWithBearerToken("set_user_preferences", value, "put");
        }}
      >
        <Spacer className="pb-5">
          <h6 className="text-gray-700 font-bold">
            What&apos;s your main Goal?
          </h6>
          <p className="text-gray text-sm">
            This decides what shows up on your dashboard. You can change it
            later.
          </p>
        </Spacer>

        <Spacer className="pb-5">
          <p className="font-medium mb-0.5">Goal</p>
          <FormControl name="boardId" required>
            <Select
              setFilled={() => setFilledValue(true)}
              optionLabel="-- Select your goal --"
              name="course"
              id="course"
              options={boards ? boards.data : []}
              className="text-gray bg-gray-50 border"
              required
            />
          </FormControl>
        </Spacer>

        <Spacer className="pb-5">
          <p className="font-medium mb-0.5">I joined Clamphook to..</p>
          <FormControl name="reasonToJoin" defaultValue={[]}>
            <CheckBoxGroup
              setFilled={() => setFilledValue(true)}
              className="checkBoxGroupItem"
            >
              <CheckBoxItem
                checkBoxWrapperStyle="mb-1"
                className="checkboxGroupItem"
                value="class"
              >
                Classroom
              </CheckBoxItem>
              <CheckBoxItem
                checkBoxWrapperStyle="mb-1"
                className="checkboxGroupItem"
                value="exam"
              >
                Test
              </CheckBoxItem>
              <CheckBoxItem
                checkBoxWrapperStyle="mb-1"
                className="checkboxGroupItem"
                value="resources"
              >
                Resources
              </CheckBoxItem>
              <CheckBoxItem
                checkBoxWrapperStyle="mb-1"
                className="checkboxGroupItem"
                value="community"
              >
                Community and Forums
              </CheckBoxItem>
            </CheckBoxGroup>
          </FormControl>
        </Spacer>

        <Spacer>
          <Button type="submit" block disabled={loading || !filledValue}>
            {loading ? (
              <div>
                <span className="mr-3">Saving...</span>
                <ButtonSpinner />
              </div>
            ) : (
              <span>Continue</span>
            )}
          </Button>
        </Spacer>
      </Form>
    </div>
  );
}

export default Preferences;
